import { Schema } from "effect"
import { AgentKind, Effort, ModelProvider, RecordMeta } from "../common"
import {
  AgentId,
  AreaId,
  CredentialId,
  Handle,
  IntegrationId,
  Markdown,
  Millis,
  PolicyId,
  ProfileId,
  ProjectId,
  Rank,
  Slug,
  UserId,
} from "../ids"
import { PlacementDefaults, Requires } from "../placement"
import { AccessRequest } from "../runner"
import { Hands } from "./task"

// #region area
/** Written by people; read by the area's (or agent's) runs on every turn. Revisions are events with before/after. */
export const Charter = Schema.Struct({ mission: Schema.String, body: Markdown })

export const IntegrationGrant = Schema.Struct({
  integrationId: IntegrationId,
  read: Schema.Array(Schema.String), // tool names
  write: Schema.Array(Schema.String),
  /** Only the publish step may execute these, after a human approved the exact call. Never in a worker's grant. */
  outward: Schema.Array(Schema.String),
  grantedBy: UserId,
  grantedAt: Millis,
})

/**
 * Rule of Two archetype for runs in this area (Research/SEO = untrusted + external). Any two may be true. All three
 * are allowed only because every external effect is then approved by a person: agents never hold outward_action,
 * `outward` tools are granted only with externalEffects, and with all three powers on, nothing but person-approved
 * `outward` tools may write (decide rejects `write` grants).
 */
export const AreaPowers = Schema.Struct({
  untrustedInput: Schema.Boolean,
  privateData: Schema.Boolean,
  externalEffects: Schema.Boolean,
})

export const AreaInterface = Schema.Struct({ key: Slug, title: Schema.String, description: Markdown })

export const Area = Schema.Struct({
  ...RecordMeta,
  id: AreaId,
  slug: Slug,
  name: Schema.String, // "Growth"
  color: Schema.String, // lane colour token
  rank: Rank, // lane order
  charter: Charter,
  /** Accepts cross-area requests and, by default, owns new initiatives here. */
  intakeAgentId: Schema.NullOr(AgentId),
  policyId: Schema.NullOr(PolicyId), // null = project policy
  grants: Schema.Array(IntegrationGrant), // Phase 2 (tool gateway); agents and workers in the area inherit them
  placement: PlacementDefaults, // e.g. Release requires xcode@26; Growth prefers trust=cloud
  powers: AreaPowers,
  wip: Schema.Struct({ maxActiveInitiatives: Schema.Int }), // default 1
  interfaces: Schema.Struct({
    publishes: Schema.Array(AreaInterface),
    consumes: Schema.Array(Schema.Struct({ areaId: AreaId, key: Slug })),
  }),
  template: Schema.NullOr(Schema.Struct({ templateId: Schema.String, version: Schema.Int })),
  status: Schema.Literals(["active", "archived"]),
})
// Rules, facts, learnings and playbooks of an area are MemoryItems with scope {area}. Not fields here.
// #endregion area

// #region agent
/** Where the agent thinks. The body (its Agent DO) never moves. No silent fallback between minds. */
export const Mind = Schema.TaggedUnion({
  /** Default. Turns run inside the Agent DO via the provider API (streamText) with in-process graph tools. */
  platform: {
    provider: ModelProvider,
    model: Schema.String, // pinned per agent (model brittleness guardrail)
    credential: Schema.TaggedUnion({ workspace_default: {}, credential: { credentialId: CredentialId } }),
  },
  /** The local CLI on a runner, on its owner's subscription. `requires.runner` pins a machine; turns wait while it is away. */
  runner: {
    agent: AgentKind,
    requires: Requires,
    profileId: Schema.NullOr(ProfileId),
    model: Schema.NullOr(Schema.String),
    effort: Schema.NullOr(Effort),
    /** The agent's turns (settings wave S8): absent or {mcp: "minga_only"} = Minga's tools only; {mcp: "cli"} = the
     *  CLI's MCP tools too, switched on by a person (core K1). */
    access: Schema.optionalKey(AccessRequest),
  },
  /** Later (Managed Agents). Present so adding it needs no migration. */
  hosted: { vendor: Schema.Literals(["anthropic_managed"]), model: Schema.String },
})
export type Mind = typeof Mind.Type

export const AgentRole = Schema.Literals(["mayor", "lead", "role"])

/**
 * The agent's identity and configuration. Project agents live in the Project DO roster (so policy is checked in
 * the same transaction as graph edits); the Mayor's record lives in the Workspace DO. Living state (chat, inbox,
 * notebook, monitors, session mirror) lives in the agent's own Agent DO. Presence is derived, never stored.
 */
export const Agent = Schema.Struct({
  ...RecordMeta, // ownerId = the principal of every run this agent starts; only the owner (or an admin) steers it
  id: AgentId,
  role: AgentRole,
  projectId: Schema.NullOr(ProjectId), // null for the Mayor
  /** Role agents belong to one area, or to none (project-wide, settings wave K7); the lead and Mayor to none. */
  areaId: Schema.NullOr(AreaId),
  name: Schema.String, // "Mara"
  handle: Handle, // @mara
  title: Schema.String, // "SEO strategist"
  avatar: Schema.Struct({ emoji: Schema.NullOr(Schema.String), color: Schema.String }),
  charter: Charter,
  mind: Mind,
  /** Legacy worker defaults: a last fallback after the task and the project's worker presets (settings wave §5.5). */
  hands: Hands,
  policyId: PolicyId, // agent-level override; effective = project ⊕ area ⊕ agent
  /** Minimum interval between turns caused by non-human wakes. Human messages bypass it. */
  wake: Schema.Struct({ debounceMs: Schema.Int }),
  status: Schema.Literals(["active", "paused", "retired"]),
})
export type Agent = typeof Agent.Type
// #endregion agent

// #region policy
/** The closed set of things a policy can talk about. One capability per graph command family. */
export const Capability = Schema.Literals([
  "create_goal",
  "update_goal",
  "change_kpi_target",
  "define_kpi",
  "create_area",
  "update_area",
  "hire_agent",
  "update_agent",
  "create_initiative",
  "update_initiative",
  "start_initiative",
  "abandon_initiative",
  "create_task",
  "update_task",
  "revise_contract",
  "cancel_task",
  "complete_task", // decision 0032: close an open task as done with evidence (CompleteTask)
  "reprioritize",
  "assign",
  "add_dependency",
  "remove_dependency",
  "spawn_workers",
  "propose_memory",
  "curate_memory",
  // forced to `never` for every agent (structural): no tool exists, and decide rejects anyway
  "outward_action",
  "edit_policy",
  "grant_integration",
])
export type Capability = typeof Capability.Type

/** Typed constraints, evaluated against the current state by core (deterministic and replayable). */
export const RuleConstraints = Schema.Struct({
  under: Schema.optionalKey(Schema.Literals(["existing_goal", "own_area", "own_initiative", "own_task"])),
  maxActive: Schema.optionalKey(Schema.Int), // e.g. create_initiative: at most 5 active
  maxConcurrent: Schema.optionalKey(Schema.Int), // spawn_workers: live worker runs for this agent
  to: Schema.optionalKey(Schema.Literals(["role_agents", "own_area_agents"])), // assign
  ifWorkDone: Schema.optionalKey(Schema.Boolean), // abandon_initiative: { if: work_done > 0 }
  maxDepth: Schema.optionalKey(Schema.Literals([0, 1, 2])), // create_task (spawn_subtask)
})
export const PolicyRule = Schema.Struct({ capability: Capability, when: Schema.optionalKey(RuleConstraints) })

export const PolicyDocument = Schema.Struct({
  allow: Schema.Array(PolicyRule),
  propose: Schema.Array(PolicyRule),
  never: Schema.Array(Capability),
})

export const Policy = Schema.Struct({
  ...RecordMeta,
  id: PolicyId,
  subject: Schema.TaggedUnion({ project: {}, area: { areaId: AreaId }, agent: { agentId: AgentId } }),
  /** Versioned. Rollback = a new revision whose document equals an older one (`restoredFrom`). */
  revision: Schema.Int,
  document: PolicyDocument,
  restoredFrom: Schema.NullOr(Schema.Int),
})
// #endregion policy
